import type { Chart, Plugin, TooltipModel } from "chart.js";
import { formatNumberWithSpaces } from "./formatNumberWithSpaces";

export const getOrCreateLegendList = (_chart: Chart, id: string) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer && legendContainer.querySelector("ul");

  if (!listContainer) {
    listContainer = document.createElement("ul");
    listContainer.style.display = "flex";
    listContainer.style.flexDirection = "row";

    legendContainer && legendContainer.appendChild(listContainer);
  }

  return listContainer;
};

type LegendItem = {
  text: string;
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  fontColor: string;
  hidden: boolean;
  index: number;
  datasetIndex: number;
  amount: string;
};

export const htmlLegendPlugin: Plugin = {
  id: "htmlLegend",
  afterUpdate(chart: Chart, _args: unknown, options: { containerID: string }) {
    const ul = getOrCreateLegendList(chart, options.containerID);

    while (ul.firstChild) {
      ul.firstChild.remove();
    }

    const items = (
      chart.options.plugins?.legend?.labels as unknown as {
        generateLabels: (c: Chart) => LegendItem[];
      }
    ).generateLabels(chart);

    items.forEach((item: LegendItem) => {
      const li = document.createElement("li");
      // li.style.alignItems = "center";
      li.style.cursor = "pointer";
      li.style.display = "flex";
      li.style.flexDirection = "row";
      // li.style.marginLeft = "10px";

      li.onclick = () => {
        const { type } = chart.config as unknown as { type: string };
        if (type === "pie" || type === "doughnut") {
          // Pie and doughnut charts only have a single dataset and visibility is per item
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
        }
        chart.update();
      };

      // Color box
      const boxSpan = document.createElement("span");
      boxSpan.style.background = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.borderWidth = item.lineWidth + "px";
      boxSpan.style.display = "inline-block";
      boxSpan.style.height = "13px";
      boxSpan.style.width = "13px";
      boxSpan.style.marginRight = "10px";
      boxSpan.style.marginTop = "10px";

      // Text
      const textContainer = document.createElement("p");
      textContainer.style.color = item.fontColor;
      textContainer.style.textDecoration = item.hidden ? "line-through" : "";
      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      // SubTitle
      const subTitleContainer = document.createElement("strong");
      subTitleContainer.style.color = item.fontColor;
      subTitleContainer.style.textDecoration = item.hidden ? "line-through" : "";
      const subtitle = document.createTextNode(item.amount);
      subTitleContainer.appendChild(subtitle);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      textContainer.appendChild(subTitleContainer);
      ul.appendChild(li);
    });
  },
};

const getOrCreateTooltip = (chart: Chart) => {
  let tooltipEl = (chart.canvas.parentNode as HTMLElement).querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.style.background = "rgba(0, 0, 0, 0.7)";
    tooltipEl.style.borderRadius = "3px";
    tooltipEl.style.color = "white";
    tooltipEl.style.opacity = "1";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.transform = "translate(-50%, 0)";
    tooltipEl.style.transition = "all .1s ease";

    const table = document.createElement("table");
    table.style.margin = "0px";

    tooltipEl.appendChild(table);
    (chart.canvas.parentNode as HTMLElement).appendChild(tooltipEl);
  }

  return tooltipEl;
};

export const externalTooltipHandler = (
  context: { chart: Chart; tooltip: TooltipModel<"bar" | "line" | "pie" | "doughnut"> },
  currencySymbol: string,
) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b: { lines: string[] }) => b.lines);

    const tableHead = document.createElement("thead");

    titleLines.forEach((title: string) => {
      const tr = document.createElement("tr");
      // tr.style.borderWidth = 0;

      const th = document.createElement("th");
      // th.style.borderWidth = 0;
      const text = document.createTextNode(title);

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    const tableBody = document.createElement("tbody");
    bodyLines.forEach((body: string[], i: number) => {
      const colors = tooltip.labelColors[i];

      const span = document.createElement("span");
      span.style.background = colors.backgroundColor as string;
      span.style.borderColor = colors.borderColor as string;
      span.style.borderWidth = "2px";
      span.style.marginRight = "10px";
      span.style.height = "10px";
      span.style.width = "10px";
      span.style.borderRadius = "10px";
      span.style.display = "inline-block";

      const tr = document.createElement("tr");
      tr.style.backgroundColor = "inherit";
      // tr.style.borderWidth = 0;

      const td = document.createElement("td");
      // td.style.borderWidth = 0;

      const text = document.createTextNode(
        `${formatNumberWithSpaces(body.join(", "))} ${currencySymbol}`,
      );

      td.appendChild(span);
      td.appendChild(text);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector("table");
    if (tableRoot) {
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove();
      }
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);
    }
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = "1";
  tooltipEl.style.left = positionX + tooltip.caretX + "px";
  tooltipEl.style.top = positionY + tooltip.caretY + "px";
  tooltipEl.style.font = (tooltip.options.bodyFont as unknown as { string: string }).string;
  tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";
  tooltipEl.style.width = "100px";
  tooltipEl.className = "tooltip";
};
